<script lang="ts">
	import type { ComponentSectionsTable } from '$lib/graphql/strapiApi';
	import { Button } from '$lib/components/ui/button';
	import { cn } from '$lib/utils.js';

	let { content }: { content: ComponentSectionsTable } = $props();

	let columns = $derived(content.columns?.filter((x) => x !== null) ?? []);
	let rows = $derived(content.rows?.filter((x) => x !== null) ?? []);
</script>

<div class="flex flex-col gap-6">
	{#if content.title}
		<div class="text-center">
			<h2 class="text-3xl font-bold tracking-tight text-gray-900">{content.title}</h2>
			{#if content.description}
				<p class="mt-2 text-lg text-gray-600">{content.description}</p>
			{/if}
			{#if content.richDescription?.body}
				<div class="prose mx-auto mt-4">{@html content.richDescription.body}</div>
			{/if}
		</div>
	{/if}

	<div class="overflow-x-auto">
		<div class="min-w-full">
			<table class="w-full table-auto border-collapse">
				<!-- Header Row -->
				<thead>
					<tr class="border-b border-gray-200">
						{#each columns as column}
							<th
								class={cn(
									'px-4 py-6 text-left align-top',
									column.highlighted ? 'border-l-4 border-l-mvmnt bg-mvmnt/10' : '',
									'first:pl-6 last:pr-6'
								)}
							>
								<div class="flex flex-col items-start gap-2">
									{#if column.titleImage}
										<img
											src={column.titleImage.url}
											alt={column.titleImage.alternativeText || column.title || ''}
											class="h-8 w-auto"
										/>
									{/if}
									{#if column.title}
										<span
											class={cn(
												'text-sm font-bold text-gray-900',
												column.highlighted ? 'text-lg' : ''
											)}
										>
											{column.title}
										</span>
									{/if}
									{#if column.button}
										<Button
											size="sm"
											variant={column.button.type === 'primary' ? 'default' : 'secondary'}
											onclick={() => {
												if (column.button?.url) {
													if (column.button.newTab) {
														window.open(column.button.url, '_blank');
													} else {
														window.location.href = column.button.url;
													}
												}
											}}
										>
											{column.button.text}
										</Button>
									{/if}
								</div>
							</th>
						{/each}
					</tr>
				</thead>

				<!-- Data Rows -->
				<tbody>
					{#each rows as row}
						<tr class="border-b border-gray-100 transition-colors hover:bg-gray-50/50">
							{#each columns as column, columnIndex}
								{@const value = row.values?.[columnIndex]}
								<td
									class={cn(
										'px-4 py-4 align-top',
										column.highlighted ? 'border-l-4 border-l-mvmnt bg-mvmnt/5' : '',
										'first:pl-6 last:pr-6',
										column.bold ? 'font-bold' : ''
									)}
								>
									{#if value?.text}
										{#if value.url}
											<a
												href={value.url}
												class="text-sm text-gray-700 underline decoration-dotted transition-colors hover:text-mvmnt"
											>
												{value.text}
											</a>
										{:else}
											<span
												class={cn('text-sm text-gray-700', column.highlighted ? 'font-medium' : '')}
											>
												{value.text}
											</span>
										{/if}
									{/if}
								</td>
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
	</div>
</div>
